<template>
  <div>
    <div class="max-w-5xl mx-auto sm:px-6 lg:px-8 pt-8 pb-12">
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-white mb-3">Meu Perfil</h1>
        <p class="text-[#9CA3AF] leading-relaxed text-sm">
          Informações da sua conta e taxas da plataforma.
        </p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">

        <!-- Informações da Conta -->
        <div class="bg-white/5 rounded-xl overflow-hidden">
          <div class="flex items-center gap-3 px-6 py-5 border-b border-white/5">
            <div class="bg-emerald-500/20 p-2.5 rounded-lg">
              <Building2 class="h-5 w-5 text-emerald-400" />
            </div>
            <h2 class="text-base font-semibold text-white">Informações da Conta</h2>
          </div>

          <div class="divide-y divide-white/5">
            <div class="flex items-center justify-between px-6 py-3.5">
              <span class="text-sm text-zinc-400">Nome</span>
              <span class="text-sm font-medium text-white">{{ org.name || '—' }}</span>
            </div>
            <div class="flex items-center justify-between px-6 py-3.5">
              <span class="text-sm text-zinc-400">E-mail</span>
              <span class="text-sm font-medium text-white truncate max-w-[60%]">{{ org.contactEmail || '—' }}</span>
            </div>
            <div v-if="org.document" class="flex items-center justify-between px-6 py-3.5">
              <span class="text-sm text-zinc-400">Documento</span>
              <span class="text-sm font-medium text-white">{{ formatDocument(org.document) }}</span>
            </div>
            <!-- <div class="flex items-center justify-between px-6 py-3.5">
              <span class="text-sm text-zinc-400">Status</span>
              <span
                class="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full"
                :class="org.status === 'active' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'"
              >
                <span class="w-1.5 h-1.5 rounded-full" :class="org.status === 'active' ? 'bg-emerald-400' : 'bg-red-400'" />
                {{ org.status === 'active' ? 'Ativo' : 'Inativo' }}
              </span>
            </div> -->
            <div class="flex items-center justify-between px-6 py-3.5">
              <span class="text-sm text-zinc-400">Senha</span>
              <Button
                variant="ghost"
                size="sm"
                class="text-xs text-zinc-400 hover:text-white gap-1.5 -mr-2 h-7"
                @click="openChangePassword"
              >
                <KeyRound class="h-3.5 w-3.5" />
                Alterar
              </Button>
            </div>
          </div>
        </div>

        <!-- Taxas -->
        <div class="bg-white/5 rounded-xl overflow-hidden">
          <div class="flex items-center gap-3 px-6 py-5 border-b border-white/5">
            <div class="bg-emerald-500/20 p-2.5 rounded-lg">
              <Percent class="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <h2 class="text-base font-semibold text-white">Taxas da Plataforma</h2>
              <p class="text-xs text-zinc-500 mt-0.5">Valor retido sobre cada venda processada</p>
            </div>
          </div>

          <div v-if="!isLoading" class="divide-y divide-white/5">
            <!-- Taxa Bot Padrão -->
            <div class="px-6 py-4">
              <div class="flex items-center gap-2 mb-3">
                <div class="bg-violet-500/15 p-1.5 rounded-md">
                  <Bot class="h-4 w-4 text-violet-400" />
                </div>
                <span class="text-sm font-semibold text-zinc-200">Taxa do Bot Padrão</span>
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div class="bg-white/3 rounded-lg px-4 py-3">
                  <p class="text-xs text-zinc-500 mb-1">Percentual</p>
                  <span class="text-xl font-bold text-white">
                    {{ firstIntegration?.splitPercentage != null ? firstIntegration.splitPercentage + '%' : '—' }}
                  </span>
                </div>
                <div class="bg-white/3 rounded-lg px-4 py-3">
                  <p class="text-xs text-zinc-500 mb-1">Valor fixo adicional</p>
                  <span class="text-xl font-bold text-white">
                    {{ firstIntegration?.splitAmount != null ? formatCurrency(firstIntegration.splitAmount) : '—' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Taxa Bot Fluxo de Chat -->
            <div class="px-6 py-4">
              <div class="flex items-center gap-2 mb-3">
                <div class="bg-emerald-500/15 p-1.5 rounded-md">
                  <GitBranch class="h-4 w-4 text-emerald-400" />
                </div>
                <span class="text-sm font-semibold text-zinc-200">Taxa do Bot Fluxo de Chat</span>
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div class="bg-white/3 rounded-lg px-4 py-3">
                  <p class="text-xs text-zinc-500 mb-1">Percentual</p>
                  <span class="text-xl font-bold text-white">0%</span>
                </div>
                <div class="bg-white/3 rounded-lg px-4 py-3">
                  <p class="text-xs text-zinc-500 mb-1">Valor fixo por venda</p>
                  <span class="text-xl font-bold text-white">
                    {{ firstIntegration?.flowSplitAmount != null ? formatCurrency(firstIntegration.flowSplitAmount + firstIntegration.splitAmount) : '—' }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="flex items-center justify-center py-14">
            <Loader2 class="w-5 h-5 animate-spin text-zinc-500" />
          </div>
        </div>

        <!-- Notificações -->
        <div class="bg-white/5 rounded-xl overflow-hidden lg:col-span-2">
          <div class="flex items-center gap-3 px-6 py-5 border-b border-white/5">
            <div class="bg-emerald-500/20 p-2.5 rounded-lg">
              <Bell class="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <h2 class="text-base font-semibold text-white">Notificações</h2>
              <p class="text-xs text-zinc-500 mt-0.5">Receba um aviso no navegador quando algo importante acontecer.</p>
            </div>
          </div>

          <div class="p-6 space-y-6">
            <div
              v-if="notificationPermission !== 'granted'"
              class="flex flex-wrap items-center justify-between gap-3 bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-3"
            >
              <div class="flex items-center gap-2 text-amber-300 text-sm">
                <BellOff class="h-4 w-4 flex-shrink-0" />
                Ative as notificações do navegador para receber os avisos.
              </div>
              <Button size="sm" @click="handleEnableNotifications">Ativar notificações</Button>
            </div>

            <div>
              <Label class="mb-3 block">Som de notificação de venda</Label>
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <button
                  v-for="sound in soundOptions"
                  :key="sound.id"
                  type="button"
                  @click="handleSelectSound(sound.id)"
                  class="flex flex-col items-center gap-2 rounded-lg border px-3 py-3 transition-colors"
                  :class="notificationSound === sound.id ? 'border-emerald-500 bg-emerald-500/10' : 'border-white/10 bg-white/5 hover:bg-white/10'"
                >
                  <Volume2 class="h-4 w-4" :class="notificationSound === sound.id ? 'text-emerald-400' : 'text-zinc-400'" />
                  <span class="text-xs" :class="notificationSound === sound.id ? 'text-white' : 'text-zinc-400'">{{ sound.label }}</span>
                </button>
              </div>
            </div>

            <div class="divide-y divide-white/5 -mx-6 border-y border-white/5">
              <div class="flex items-center justify-between px-6 py-3.5">
                <div>
                  <p class="text-sm font-medium text-white">Venda aprovada</p>
                  <p class="text-xs text-zinc-500">Avisar quando um pagamento for confirmado.</p>
                </div>
                <Switch v-model:checked="notificationPreferences.sale" />
              </div>
              <div class="flex items-center justify-between px-6 py-3.5">
                <div>
                  <p class="text-sm font-medium text-white">Pix gerado (venda pendente)</p>
                  <p class="text-xs text-zinc-500">Avisar quando um cliente gerar um Pix aguardando pagamento.</p>
                </div>
                <Switch v-model:checked="notificationPreferences.pendingSale" />
              </div>
              <div class="flex items-center justify-between px-6 py-3.5">
                <div>
                  <p class="text-sm font-medium text-white">Bot fora do ar</p>
                  <p class="text-xs text-zinc-500">Avisar quando um dos seus bots parar de responder.</p>
                </div>
                <Switch v-model:checked="notificationPreferences.botDown" />
              </div>
            </div>

            <div class="flex justify-end">
              <Button :disabled="isSavingNotifications" @click="handleSaveNotifications">
                <Loader2 v-if="isSavingNotifications" class="w-4 h-4 animate-spin mr-2" />
                Salvar notificações
              </Button>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>

  <!-- Modal Alterar Senha -->
  <Dialog v-model:open="isChangePasswordOpen">
    <DialogScrollContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Alterar senha</DialogTitle>
        <DialogDescription>
          Preencha os campos abaixo para redefinir sua senha de acesso.
        </DialogDescription>
      </DialogHeader>

      <div class="grid gap-4 py-2">
        <div class="grid gap-2">
          <Label>Senha atual <span class="text-red-500">*</span></Label>
          <Input
            type="password"
            v-model="passwordForm.passwordOld"
            placeholder="••••••••"
            autocomplete="current-password"
          />
        </div>
        <div class="grid gap-2">
          <Label>Nova senha <span class="text-red-500">*</span></Label>
          <Input
            type="password"
            v-model="passwordForm.password"
            placeholder="••••••••"
            autocomplete="new-password"
          />
        </div>
        <div class="grid gap-2">
          <Label>Confirmar nova senha <span class="text-red-500">*</span></Label>
          <Input
            type="password"
            v-model="passwordForm.passwordConfirm"
            placeholder="••••••••"
            autocomplete="new-password"
          />
        </div>

        <p v-if="passwordError" class="flex items-center gap-2 text-xs text-red-400">
          <AlertCircle class="h-3.5 w-3.5 flex-shrink-0" />
          {{ passwordError }}
        </p>
      </div>

      <DialogFooter>
        <Button variant="ghost" class="text-white" @click="isChangePasswordOpen = false">Cancelar</Button>
        <Button
          :disabled="isChangingPassword || !passwordForm.passwordOld || !passwordForm.password || !passwordForm.passwordConfirm"
          @click="handleChangePassword"
        >
          <Loader2 v-if="isChangingPassword" class="w-4 h-4 animate-spin mr-2" />
          Salvar
        </Button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>

</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { AlertCircle, Bell, BellOff, Bot, Building2, GitBranch, KeyRound, Loader2, Percent, Volume2 } from 'lucide-vue-next'
import Cookies from 'js-cookie'
import { usePaymentGatewayIntegration } from '@/composables/usePaymentGatewayIntegration'
import { useAuth, NotificationPreferences } from '@/composables/useAuth'
import { usePushNotification, playNotificationSound } from '@/composables/usePushNotification'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/components/ui/toast'
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogScrollContent,
  DialogTitle,
} from '@/components/ui/dialog'

const rawOrg = Cookies.get('organization')
  ? JSON.parse(Cookies.get('organization') as string)
  : {}
const org = rawOrg

const { list } = usePaymentGatewayIntegration()
const isLoading = ref(true)
const firstIntegration = ref<any>(null)

const { changePassword, getProfile, updateNotificationSettings } = useAuth()
const { requestPermission } = usePushNotification()
const { toast } = useToast()

const isChangePasswordOpen = ref(false)
const isChangingPassword = ref(false)
const passwordError = ref('')
const passwordForm = ref({ passwordOld: '', password: '', passwordConfirm: '' })

const soundOptions = [
  { id: 'cashregister', label: 'Caixa registradora' },
  { id: 'cash2', label: 'Dinheiro 1' },
  { id: 'cash3', label: 'Dinheiro 2' },
]

const notificationPermission = ref<NotificationPermission>(
  typeof Notification !== 'undefined' ? Notification.permission : 'denied'
)
const notificationPreferences = ref<NotificationPreferences>({ sale: true, pendingSale: true, botDown: true })
const notificationSound = ref('cashregister')
const isSavingNotifications = ref(false)

function handleSelectSound(soundId: string) {
  notificationSound.value = soundId
  playNotificationSound(soundId)
}

async function handleEnableNotifications() {
  await requestPermission()
  if (typeof Notification !== 'undefined') {
    notificationPermission.value = Notification.permission
  }
}

async function handleSaveNotifications() {
  isSavingNotifications.value = true
  try {
    await updateNotificationSettings({
      notification: notificationPreferences.value,
      notificationSound: notificationSound.value
    })
    toast({ title: 'Notificações atualizadas com sucesso', class: 'bg-green-500 text-white', duration: 2000 })
  } catch {
    toast({ title: 'Erro ao atualizar notificações', variant: 'destructive', duration: 2500 })
  } finally {
    isSavingNotifications.value = false
  }
}

function openChangePassword() {
  passwordForm.value = { passwordOld: '', password: '', passwordConfirm: '' }
  passwordError.value = ''
  isChangePasswordOpen.value = true
}

function formatDocument(doc: string): string {
  const d = doc.replace(/\D/g, '')
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  return doc
}

function formatCurrency(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

async function handleChangePassword() {
  passwordError.value = ''

  if (passwordForm.value.password.length < 8) {
    passwordError.value = 'A nova senha deve ter no mínimo 8 caracteres.'
    return
  }

  if (passwordForm.value.password !== passwordForm.value.passwordConfirm) {
    passwordError.value = 'A nova senha e a confirmação não coincidem.'
    return
  }

  isChangingPassword.value = true
  try {
    await changePassword(passwordForm.value)
    isChangePasswordOpen.value = false
    toast({ title: 'Senha alterada com sucesso', class: 'bg-green-500 text-white', duration: 2000 })
  } catch {
    passwordError.value = 'Senha atual incorreta ou erro ao alterar. Tente novamente.'
  } finally {
    isChangingPassword.value = false
  }
}

onMounted(async () => {
  try {
    const integrations = await list()
    if (integrations?.length) firstIntegration.value = integrations[0]
  } catch {
    // sem integração
  } finally {
    isLoading.value = false
  }
})

onMounted(async () => {
  try {
    const profile = await getProfile()
    notificationPreferences.value = {
      sale: profile.notification?.sale ?? true,
      pendingSale: profile.notification?.pendingSale ?? true,
      botDown: profile.notification?.botDown ?? true
    }
    notificationSound.value = profile.notificationSound || 'cashregister'
  } catch {
    // mantém os valores padrão
  }
})
</script>
